import React, { useState, useEffect } from "react";
import "../../assets/NewFiles/Css/ProjectsDashboard.css";
import { useNavigate } from "react-router-dom";
import api from "../../axios/api";
import { FaPenSquare, FaCheck, FaImage, FaUserAlt, FaMap, FaPlus, FaSearch, FaTimes, FaTachometerAlt, FaEye } from "react-icons/fa";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Grid,
  useTheme,
} from "@mui/material";
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";
import CommonGrid from "../common/Grid";

const ProjectsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  
  // For responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/project/getProjects");

      const projectsData = Array.isArray(response.data)
        ? response.data
        : response.data.projects
        ? response.data.projects
        : [response.data];

      setProjects(projectsData);
      setFilteredProjects(projectsData);

      const orgSet = new Set();
      projectsData.forEach(project => {
        if (project.organizationName) {
          const orgs = project.organizationName.split(',');
          orgs.forEach(org => orgSet.add(org.trim()));
        }
      });
      setOrganizations(Array.from(orgSet).sort());
    } catch (error) {
      console.error("❌ Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    let result = projects;
    if (selectedOrg) {
      result = result.filter(project =>
        project?.organizationName?.split(',').map(o => o.trim()).includes(selectedOrg)
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter(project =>
        (project?.projectName && project.projectName.toLowerCase().includes(q)) ||
        (project?.organizationName && project.organizationName.toLowerCase().includes(q)) ||
        (project?.location && project.location.toLowerCase().includes(q))
      );
    }
    setFilteredProjects(result);
    setPage(1);
  }, [searchTerm, selectedOrg, projects]);

  const handleOrgChange = (event) => {
    setSelectedOrg(event.target.value);
  };

  const updateProjectStatus = async (projectId) => {
    setIsLoading(true);
    try {
      const response = await api.post(
        `/api/project/updateProjectStatus/${projectId}`
      );
      if (response.status === 200) {
        fetchProjects();
      }
    } catch (error) {
      console.error("Error updating project status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Calculate pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);
  const pageCount = Math.ceil(filteredProjects.length / rowsPerPage);

  useEffect(() => {
    fetchProjects(); 
  }, []);

  // Card view for mobile
  const ProjectCard = ({ project }) => {
    return (
      <Card sx={{ 
        mb: 2, 
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
        }
      }}>
        <CardContent>
          <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold', color: '#002F43' }}>
            {project.name}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Duration:</strong>
            </Typography>
            <Typography variant="body2">
              {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Progress:</strong>
            </Typography>
            <Chip 
              label={`${project.total_trees_planted} / ${project.target}`} 
              size="small"
              sx={{ 
                bgcolor: '#7CB342',
                fontWeight: 'medium'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Address:</strong>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%' }}>
              {project.address}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%' }}>
              {project.organizationName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/overview/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0f2fe' } }}
              title="Project Dashboard & Species Distribution"
            >
              <FaTachometerAlt color="#0284c7" size="16px" />
            </IconButton>

            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              title="Edit Project"
            >
              <FaPenSquare color="#475569" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/updateprojectgallery/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              title="Manage Gallery"
            >
              <FaImage color="#00a9d3" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/projectArea/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              title="Manage Area"
            >
              <FaMap color="#00a9d3" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => updateProjectStatus(project.project_id)}
              sx={{ bgcolor: '#f5f5f5' }}
              title={project.is_active === "0" ? "Activate Project" : "Deactivate Project"}
            >
              <FaCheck color={project.is_active === "0" ? "red" : "#83d300"} size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectcontributors/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              title="Contributors"
            >
              <FaUserAlt color="gray" size="16px" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Loading overlay component
  const LoadingOverlay = () => (
    <div className="loading-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Loader />
    </div>
  );

  return (
    <div>    <div className="projects-dashboard" style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-7 bg-emerald-500 rounded-full flex-shrink-0" />
          <h1 className="text-2xl sm:text-[1.75rem] font-bold text-[#002F43] tracking-tight m-0 leading-tight">
            Green Projects
          </h1>
        </div>

        {/* Modern Search & Filter Box */}
        <div className="bg-white p-3.5 sm:p-4 rounded-xl shadow-xs border border-gray-200/80 mb-5">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Search Input Bar */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-600 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Search projects by name, location, or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full !pl-10 !pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
                style={{ paddingLeft: '40px', paddingRight: '40px' }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1 cursor-pointer"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Organization Filter & Create Button */}
            <div className="flex items-center gap-2 flex-wrap">
              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel id="org-select-label">Organization</InputLabel>
                <Select
                  labelId="org-select-label"
                  value={selectedOrg}
                  label="Organization"
                  onChange={handleOrgChange}
                  sx={{ bgcolor: 'grey.50', borderRadius: 1 }}
                >
                  <MenuItem value="">All Organizations</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org} value={org}>
                      {org}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                onClick={() => navigate("/dashboard/projectdashboard/newproject")}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  fontSize: '0.8rem'
                }}
              >
                <FaPlus size="12px" />
                Create New Project
              </Button>
            </div>
          </div>
        </div>

        {/* Responsive content - Table for desktop, Cards for mobile */}
        {isMobile ? (
          // Mobile View - Card Layout
          <Box>
            {paginatedProjects.length > 0 ? (
              paginatedProjects.map((project) => (
                <ProjectCard key={project.project_id} project={project} />
              ))
            ) : (
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">No projects found</Typography>
              </Card>
            )}
          </Box>
        ) : (
          <CommonGrid
            columns={[
              {
                header: "Name",
                accessor: "name",
                sortable: true,
                render: (val) => <span style={{ fontWeight: 600,color:"black" }}>{val}</span>,
              },
              {
                header: "Duration",
                sortable: true,
                accessor: "startDate",
                render: (_, project) => (
                  <span style={{ minWidth: "150px", display: "inline-block",color:"black" }}>
                    {new Date(project.startDate).toLocaleDateString()}
                    {" - "}
                    {new Date(project.endDate).toLocaleDateString()}
                  </span>
                ),
              },
              {
                header: "Planted / Target",
                align: "center",
                sortable: true,
                accessor: "total_trees_planted",
                render: (_, project) => (
                  <Chip
                    label={`${project.total_trees_planted} / ${project.target}`}
                    size="small"
                    sx={{
                      bgcolor: "#7CB342",
                      color: "#fff",
                      fontWeight: "bold",
                    }}
                  />
                ),
              },
              {
                header: "Address",
                accessor: "address",
                sortable: true,
              },
              {
                header: "Organization",
                accessor: "organizationName",
                sortable: true,
              },
              {
                header: "Actions",
                align: "center",
                render: (_, project) => (
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      title="Project Dashboard & Species Distribution"
                      onClick={() =>
                        navigate(`/dashboard/projectdashboard/overview/${project.project_id}`)
                      }
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#e0f2fe" },
                      }}
                    >
                      <FaTachometerAlt color="#0284c7" size="16px" />
                    </IconButton>

                    <IconButton
                      size="small"
                      title="Edit Project"
                      onClick={() =>
                        navigate(`/dashboard/projectdashboard/${project.project_id}`)
                      }
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#f1f5f9" },
                      }}
                    >
                      <FaPenSquare color="#475569" size="16px" />
                    </IconButton>

                    <IconButton
                      size="small"
                      title="Manage project gallery"
                      onClick={() =>
                        navigate(
                          `/dashboard/projectdashboard/updateprojectgallery/${project.project_id}`
                        )
                      }
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#e0f7fa" },
                      }}
                    >
                      <FaImage color="#00a9d3" size="16px" />
                    </IconButton>

                    <IconButton
                      size="small"
                      title="Manage project Area"
                      onClick={() =>
                        navigate(
                          `/dashboard/projectdashboard/projectArea/${project.project_id}`
                        )
                      }
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#e0f7fa" },
                      }}
                    >
                      <FaMap color="#00a9d3" size="16px" />
                    </IconButton>

                    <IconButton
                      size="small"
                      title={
                        project.is_active === "0"
                          ? "Activate Project"
                          : "Deactivate Project"
                      }
                      onClick={() => updateProjectStatus(project.project_id)}
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": {
                          bgcolor:
                            project.is_active === "0" ? "#ffebee" : "#f1f8e9",
                        },
                      }}
                    >
                      <FaCheck
                        color={project.is_active === "0" ? "red" : "#83d300"}
                        size="16px"
                      />
                    </IconButton>

                    <IconButton
                      size="small"
                      title="Project Contributors"
                      onClick={() =>
                        navigate(
                          `/dashboard/projectcontributors/${project.project_id}`
                        )
                      }
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      <FaUserAlt color="gray" size="16px" />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            data={filteredProjects}
            variant="navy"
            pagination={true}
            pageSize={rowsPerPage}
            pageSizeOptions={[5, 10, 20, 50]}
            rowKey="project_id"
            emptyMessage="No projects found"
          />
        )}
      </div>
    </div>
    </div>

  );
};

export default ProjectsDashboard;